import { useEffect, useRef, useState } from 'react';

interface Option {
  value: string;
  label: string;
}

interface Props {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
}

export function SearchableSelect({ options, value, onChange, placeholder = '— Select —', disabled, invalid, id }: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  const selected = options.find((o) => o.value === value);
  const filtered = options.filter((o) => !search || o.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="cls-ss-wrap" ref={wrapRef}>
      <div
        id={id}
        className="cls-ss-input"
        style={{
          ...(disabled ? { opacity: 0.6, cursor: 'not-allowed' } : null),
          ...(invalid ? { borderColor: 'var(--danger)', boxShadow: '0 0 0 2px rgba(239,68,68,.15)' } : null),
        }}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        {selected?.label || <span style={{ color: 'var(--faint)' }}>{placeholder}</span>}
      </div>
      <span className="cls-ss-arrow">▾</span>
      <div className={`cls-ss-drop ${open ? 'open' : ''}`} role="listbox">
        <input
          className="cls-ss-search"
          placeholder="Type to search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus={open}
        />
        {filtered.length === 0 ? <div className="cls-ss-empty">No matches</div> : null}
        {filtered.map((o) => (
          <div
            key={o.value}
            className={`cls-ss-opt ${o.value === value ? 'selected' : ''}`}
            onClick={() => {
              onChange(o.value);
              setSearch('');
              setOpen(false);
            }}
          >
            {o.label}
          </div>
        ))}
      </div>
    </div>
  );
}
